import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-offwhite placeholder:text-white/35 focus:border-gold focus:outline-none";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.3-2.1 3.7-5.2 3.7-8.6z" />
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5.1L1.3 17.2C3.3 21.2 7.3 24 12 24z" />
    <path fill="#FBBC05" d="M5.2 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3L1.3 6.8C.5 8.4 0 10.1 0 12s.5 3.6 1.3 5.2l3.9-2.9z" />
    <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.3 0 3.3 2.8 1.3 6.8l3.9 2.9c.9-2.9 3.6-5 6.8-5z" />
  </svg>
);

export default function AdminLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(
    searchParams.get("error") === "google" ? "Google sign-in failed or that account is not authorized." : ""
  );
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) { setChecking(false); return; }
    let cancelled = false;
    api.get("/auth/me")
      .then(() => navigate("/admin", { replace: true }))
      .catch(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      navigate("/admin", { state: { user: data }, replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-ink"><p className="text-sm text-white/50">Loading…</p></div>;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-5" data-testid="admin-login-page">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-charcoal p-10">
        <Logo />
        <h1 className="mt-8 font-display text-3xl text-offwhite">Admin Login</h1>
        <p className="mt-2 text-sm text-white/50">D &amp; N Constructions content management</p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <input type="email" required data-testid="admin-email-input" className={inputCls} placeholder="Email"
            value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          <input type="password" required data-testid="admin-password-input" className={inputCls} placeholder="Password"
            value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          {error && <p className="rounded-xl border border-red-400/30 bg-red-400/10 px-4 py-3 text-xs text-red-300" role="alert" data-testid="admin-login-error">{error}</p>}
          <button type="submit" data-testid="admin-login-submit" disabled={loading}
            className="w-full rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-charcoal transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold-soft disabled:opacity-60">
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <div className="my-6 flex items-center gap-4" aria-hidden="true">
          <span className="h-px flex-1 bg-white/10" />
          <span className="text-xs font-semibold uppercase tracking-widest text-white/35">or</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <button type="button" onClick={handleGoogle} data-testid="admin-google-login-button"
          className="flex w-full items-center justify-center gap-3 rounded-full border border-white/15 bg-white px-7 py-3.5 text-sm font-bold text-charcoal transition-transform hover:-translate-y-0.5">
          <GoogleIcon /> Continue with Google
        </button>
        <p className="mt-5 text-center text-[11px] leading-relaxed text-white/35">
          Google sign-in is enabled only for the authorized admin account.
        </p>
      </div>
    </div>
  );
}
